import React, { useEffect, useState } from 'react';
import { flagOf } from '../flags';

// City + Country fields for the New Quest / Import forms, backed by the
// cities / countries dictionary (/api/places). A known city fixes its country
// (shown read-only); a new city asks for a country, suggesting existing ones,
// plus the Russian names the app shows in its country / city filter and, for
// a new country, the two-letter ISO code that draws its flag. The server adds
// the new place to the dictionary when the quest is created.
//
// `names` = { cityNameRu, countryNameRu, isoCode }, changed through
// onNamesChange(patch).
const PlaceFields = ({ city, country, onCityChange, onCountryChange, names = {}, onNamesChange = () => {} }) => {
  const [places, setPlaces] = useState({ countries: [], cities: [] });

  useEffect(() => {
    let alive = true;
    fetch('/api/places', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (alive && data) setPlaces(data); })
      .catch(err => console.error('Error fetching places:', err));
    return () => { alive = false; };
  }, []);

  const slug = city.trim().toLowerCase();
  const known = places.cities.find(c => c.slug === slug);
  const knownCountry = known ? places.countries.find(c => c.name === known.country) : null;
  const typedCountry = country.trim();
  const existingCountry = typedCountry
    ? places.countries.find(c => c.name.toLowerCase() === typedCountry.toLowerCase())
    : null;

  // A known city dictates the country.
  useEffect(() => {
    if (known && country !== known.country) onCountryChange(known.country);
  }, [known, country, onCountryChange]);

  const set = (key) => (e) => onNamesChange({ [key]: e.target.value });

  return (
    <>
      <label className="new-quest-field">
        <span className="new-quest-label">City <small>(slug, e.g. istanbul)</small></span>
        <input
          type="text"
          list="place-fields-cities"
          value={city}
          onChange={(e) => onCityChange(e.target.value)}
          placeholder="istanbul"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <datalist id="place-fields-cities">
          {places.cities.map(c => (
            <option key={c.slug} value={c.slug}>{c.name_ru ? `${c.name_ru} · ${c.country}` : c.country}</option>
          ))}
        </datalist>
      </label>

      {slug && !known && (
        <label className="new-quest-field">
          <span className="new-quest-label">City in Russian <small>(shown in the app, e.g. Стамбул)</small></span>
          <input type="text" value={names.cityNameRu || ''} onChange={set('cityNameRu')} placeholder="Стамбул" />
        </label>
      )}
      {known && !known.name_ru && (
        <label className="new-quest-field">
          <span className="new-quest-label">City in Russian <small>(the app has none for {known.slug} yet)</small></span>
          <input type="text" value={names.cityNameRu || ''} onChange={set('cityNameRu')} placeholder="Стамбул" />
        </label>
      )}

      <label className="new-quest-field">
        <span className="new-quest-label">
          Country {known ? '' : slug ? <small>(new city — pick or type its country)</small> : null}
        </span>
        {known ? (
          <div className="new-quest-readonly">
            {flagOf(knownCountry && knownCountry.iso_code)} {known.country}
            {knownCountry && knownCountry.name_ru ? ` · ${knownCountry.name_ru}` : ''}
          </div>
        ) : (
          <>
            <input
              type="text"
              list="place-fields-countries"
              value={country}
              onChange={(e) => onCountryChange(e.target.value)}
              placeholder="Turkey"
            />
            <datalist id="place-fields-countries">
              {places.countries.map(c => <option key={c.name} value={c.name}>{c.name_ru || ''}</option>)}
            </datalist>
          </>
        )}
      </label>

      {!known && typedCountry && !existingCountry && (
        <>
          <label className="new-quest-field">
            <span className="new-quest-label">Country in Russian <small>(shown in the app, e.g. Турция)</small></span>
            <input type="text" value={names.countryNameRu || ''} onChange={set('countryNameRu')} placeholder="Турция" />
          </label>
          <label className="new-quest-field">
            <span className="new-quest-label">
              ISO code <small>(two letters, draws the flag)</small> {flagOf(/^[A-Za-z]{2}$/.test(names.isoCode || '') ? names.isoCode : null)}
            </span>
            <input
              type="text"
              value={names.isoCode || ''}
              onChange={set('isoCode')}
              placeholder="TR"
              maxLength={2}
              spellCheck={false}
              autoCapitalize="characters"
            />
          </label>
        </>
      )}
    </>
  );
};

export default PlaceFields;
